import React, { useState } from "react";
import toast from "react-hot-toast";
import { ImageUp, Trash, X } from "lucide-react";

interface BasicDetailsProps {
  data: any;
  onChange: (field: string, value: any) => void;
  onImageChange: (type: "main" | "sub", file: File | null, index?: number) => void;
  onRemoveImage: (type: "main" | "sub", index?: number) => void;
  isEdit: boolean;
  onUpdate?: () => void;
  isLoading?: boolean;
}

const formStyles = {
  lableStyle: "block text-sm text-[#3F5260] font-medium mb-1",
  inputStyle:
    "w-full px-3 py-2 border border-[#D0D5DE] outline-none rounded-lg focus:ring-1 focus:ring-emerald-500 text-sm",
};

export const BasicDetailsTab: React.FC<BasicDetailsProps> = ({
  data,
  onChange,
  onImageChange,
  onRemoveImage,
  isEdit,
  onUpdate,
  isLoading,
}) => {
  const [viewingImage, setViewingImage] = useState<{
    type: "main" | "sub";
    index?: number;
    title: string;
  } | null>(null);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          <div>
            <label className={formStyles.lableStyle}>Product Name</label>
            <input
              type="text"
              value={data.name}
              onChange={(e) => onChange("name", e.target.value)}
              className={formStyles.inputStyle}
              placeholder="Enter Product Name"
            />
          </div>

          <div>
            <label className={formStyles.lableStyle}>Description</label>
            <textarea
              value={data.description}
              onChange={(e) => onChange("description", e.target.value)}
              className={`${formStyles.inputStyle} h-[120px] resize-none`}
              placeholder="Enter Product Description"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className={formStyles.lableStyle}>Tablets Per Strip / Qty</label>
              <input
                type="number"
                value={data.tabletPerStripe}
                onChange={(e) => onChange("tabletPerStripe", Number(e.target.value))}
                className={formStyles.inputStyle}
              />
            </div>
          </div>

          <div className="flex flex-col gap-6 mt-4">
            {/* Rx Required Radio Buttons */}
            <div>
              <label className={formStyles.lableStyle}>Rx Required</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 text-sm text-[#3F5260] font-medium cursor-pointer">
                  <input
                    type="radio"
                    name="isRxRequired"
                    checked={data.isRxRequired === true}
                    onChange={() => onChange("isRxRequired", true)}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3F5260] font-medium cursor-pointer">
                  <input
                    type="radio"
                    name="isRxRequired"
                    checked={data.isRxRequired === false}
                    onChange={() => onChange("isRxRequired", false)}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  No
                </label>
              </div>
            </div>

            {/* Published Radio Buttons */}
            <div>
              <label className={formStyles.lableStyle}>Published</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 text-sm text-[#3F5260] font-medium cursor-pointer">
                  <input
                    type="radio"
                    name="isPublished"
                    checked={data.isPublished === true}
                    onChange={() => onChange("isPublished", true)}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3F5260] font-medium cursor-pointer">
                  <input
                    type="radio"
                    name="isPublished"
                    checked={data.isPublished === false}
                    onChange={() => onChange("isPublished", false)}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  No
                </label>
              </div>
            </div>

            {/* Is Active Radio Buttons */}
            <div>
              <label className={formStyles.lableStyle}>Active</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 text-sm text-[#3F5260] font-medium cursor-pointer">
                  <input
                    type="radio"
                    name="isActive"
                    checked={data.isActive === true}
                    onChange={() => onChange("isActive", true)}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  Yes
                </label>
                <label className="flex items-center gap-2 text-sm text-[#3F5260] font-medium cursor-pointer">
                  <input
                    type="radio"
                    name="isActive"
                    checked={data.isActive === false}
                    onChange={() => onChange("isActive", false)}
                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                  />
                  No
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Images */}
        <div className="space-y-4">
          <label className={formStyles.lableStyle}>Product Images</label>
          <div className="p-4 border border-[#D0D5DE] rounded-xl bg-[#F9FAFB]">
            <p className="text-sm text-gray-500 mb-3 font-medium">Main Image</p>
            <div className="flex items-center gap-6">
              <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-white relative group">
                {data.mainImagePreview ? (
                  <div
                    onClick={() =>
                      setViewingImage({
                        type: "main",
                        title: "Main Product Image",
                      })
                    }
                    className="w-full h-full cursor-pointer relative group flex items-center justify-center"
                    title="Click to view or edit image"
                  >
                    <img src={data.mainImagePreview} alt="Main" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-xs font-medium px-2 py-1 bg-black/60 rounded-md">
                        Click to View
                      </span>
                    </div>
                  </div>
                ) : (
                  <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center hover:bg-gray-50">
                    <ImageUp className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-xs text-gray-500">Upload Main</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          onImageChange("main", e.target.files[0]);
                        }
                      }}
                    />
                  </label>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-500 mt-6 mb-3 font-medium">Sub Images (Up to 3)</p>
            <div className="grid grid-cols-3 gap-4">
              {[0, 1, 2].map((index) => (
                <div key={index} className="flex flex-col gap-2">
                  <div className="w-full aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-white relative group">
                    {data.subImagePreviews[index] ? (
                      <div
                        onClick={() =>
                          setViewingImage({
                            type: "sub",
                            index,
                            title: `Sub Product Image ${index + 1}`,
                          })
                        }
                        className="w-full h-full cursor-pointer relative group flex items-center justify-center"
                        title="Click to view or edit image"
                      >
                        <img src={data.subImagePreviews[index]!} alt={`Sub ${index + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white text-xs font-medium px-2 py-1 bg-black/60 rounded-md">
                            Click to View
                          </span>
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center hover:bg-gray-50">
                        <ImageUp className="w-6 h-6 text-gray-400 mb-2" />
                        <span className="text-xs text-gray-500">Upload Sub</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              onImageChange("sub", e.target.files[0], index);
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {isEdit && (
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={onUpdate}
            disabled={isLoading}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition"
          >
            {isLoading ? "Updating..." : "Update Basic Details"}
          </button>
        </div>
      )}

      {/* Image Preview & Update Modal */}
      {viewingImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="relative bg-white rounded-2xl max-w-2xl w-full p-6 flex flex-col gap-4 shadow-2xl max-h-[90vh]">
            <div className="flex items-center justify-between border-b pb-3">
              <h3 className="text-lg font-semibold text-gray-800">{viewingImage.title}</h3>
              <button
                type="button"
                onClick={() => setViewingImage(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex items-center justify-center bg-gray-50 rounded-xl p-4 overflow-hidden min-h-[250px] max-h-[55vh]">
              <img
                src={
                  viewingImage.type === "main"
                    ? data.mainImagePreview
                    : data.subImagePreviews[viewingImage.index!]
                }
                alt={viewingImage.title}
                className="max-h-[50vh] w-auto max-w-full object-contain rounded-md"
              />
            </div>

            <div className="flex items-center justify-between pt-2 border-t mt-2">
              <button
                type="button"
                onClick={() => {
                  onRemoveImage(viewingImage.type, viewingImage.index);
                  setViewingImage(null);
                }}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <Trash size={16} />
                Remove Image
              </button>

              <div className="flex items-center gap-3">
                <label className="cursor-pointer px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center gap-2">
                  <ImageUp size={16} />
                  Update Image
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        onImageChange(viewingImage.type, e.target.files[0], viewingImage.index);
                      }
                    }}
                  />
                </label>

                <button
                  type="button"
                  onClick={() => setViewingImage(null)}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

